import React, { useCallback, type SyntheticEvent } from 'react'
import { Sparkle } from 'pcln-icons'
import { useMediaQuery } from 'react-responsive'
import {
  Box,
  Heading,
  Flex,
  theme,
  Text,
  Relative,
  Button
} from 'pcln-design-system'
import {
  UpcomingHotel,
  UpcomingFlight,
  UpcomingRentalCar,
  CrossSellCard
} from '@pcln/trip-activity-components'
import useDetectBrowser from '@/hooks/useDetectBrowser'
import { trackNoInventoryXsellClick, firePennyButtonAnalytics } from './utils'

import { Booking, ProductType } from './types'

function ViewMyTrips() {
  return (
    <Button
      variation="subtle"
      size="small"
      onClick={() => window.open('/next-profile/trips')}
      mb={[2, null, null, 0]}
    >
      View My Trips
    </Button>
  )
}

function PennyButton({ offerToken }: { offerToken: string }) {
  const TextSpan = Text.span
  return (
    <Button
      variation="subtle"
      size="small"
      onClick={() => {
        firePennyButtonAnalytics()
        window.open(
          `/travel-itinerary/?offerToken=${offerToken}&openPenny=y&askPrompt=I%20need%20help%20with%20this%20booking`
        )
      }}
      mb={[2, null, null, 0]}
    >
      <Sparkle size={14} />
      <TextSpan ml={1}>Need help with this booking?</TextSpan>
    </Button>
  )
}

function UpcomingTripCard({ booking }: { booking: Booking }) {
  const itineraryUrl = `/travel-itinerary/?offerToken=${booking.offerToken}`

  const handleUpcomingTripOnClick = () => {
    if (typeof window !== 'undefined') {
      window.open(itineraryUrl)
    }
  }

  const { travelEndDateTime, travelStartDateTime, destinationCity } = booking

  if (booking.productId === 1) {
    const isOneWay = travelStartDateTime === travelEndDateTime
    const { originLocationCode, destinationLocationCode } = booking
    return (
      <UpcomingFlight
        origin={originLocationCode}
        destination={destinationLocationCode}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        isOneWay={isOneWay}
        onClick={handleUpcomingTripOnClick}
      />
    )
  }
  if (booking.productId === 5) {
    const { hotelName, destinationCountryCode } = booking
    return (
      <UpcomingHotel
        hotelName={hotelName || 'Your Upcoming Hotel Stay'}
        destination={`${destinationCity}, ${destinationCountryCode}`}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        truncateHeading
        onClick={handleUpcomingTripOnClick}
      />
    )
  }
  if (booking.productId === 8) {
    return (
      <UpcomingRentalCar
        rentalCarName="Your Upcoming Rental Car"
        destination={destinationCity}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        onClick={handleUpcomingTripOnClick}
        truncateHeading
      />
    )
  }
}

const getProductTypeById = (productId: 1 | 5 | 8) => {
  switch (productId) {
    case 1:
      return 'FLY'
    case 5:
      return 'STAY'
    case 8:
      return 'DRIVE'
    // no default
  }
}

function UpcomingTripExperiment({
  booking,
  xSellUrl,
  xSellTargetId,
  setiVariant
}: {
  booking: Booking
  xSellUrl: string
  xSellTargetId: ProductType
  setiVariant: string
}) {
  const { offerToken } = booking

  const showPennyLink = setiVariant === 'WITH_PENNY'

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg as string})`
  })

  const isBrowser = useDetectBrowser()

  const crossSellProduct = getProductTypeById(xSellTargetId)
  const primaryProduct = getProductTypeById(booking.productId)

  const crossSellOnClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      if (isBrowser) {
        trackNoInventoryXsellClick({
          event: 'select_promotion_bundle',
          product: booking.productId,
          xSellProduct: xSellTargetId,
          offerId: booking.offerId,
          destination:
            booking.destinationCity ||
            (booking.productId === 1 ? booking.destinationLocationCode : '')
        })

        window.open(xSellUrl)
      }
    },
    [booking, xSellTargetId, xSellUrl, isBrowser]
  )

  return (
    isBrowser && (
      <Relative mt={2}>
        <Flex justifyContent="space-between" px={1}>
          <Heading
            textStyle={['heading4', null, null, 'heading3']}
            color="text.heading"
            m={0}
            mb={3}
            as="h1"
          >
            Your upcoming trips
          </Heading>
          {isDesktop && (
            <Box>
              {showPennyLink && <PennyButton offerToken={offerToken} />}{' '}
              <ViewMyTrips />
            </Box>
          )}
        </Flex>
        <Flex flexDirection={['column', null, null, 'row']}>
          <Box width={[1, null, null, 1 / 2]} px={1} mb={[2, null, null, 0]}>
            <UpcomingTripCard booking={booking} />
          </Box>
          <Box width={[1, null, null, 1 / 2]} px={1} mb={[2, null, null, 0]}>
            {crossSellProduct && primaryProduct && (
              <CrossSellCard
                crossSellProduct={crossSellProduct}
                primaryProduct={primaryProduct}
                onClick={crossSellOnClick}
              />
            )}
          </Box>
          {!isDesktop && (
            <Flex flexDirection="column" px={2} mt={2} mb={4}>
              {showPennyLink && <PennyButton offerToken={offerToken} />}{' '}
              <ViewMyTrips />
            </Flex>
          )}
        </Flex>
      </Relative>
    )
  )
}

export default UpcomingTripExperiment
